import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  Popover,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { DownloadOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';

// ── Action labels ──────────────────────────────────────────────────────────────

const actionLabels: Record<string, string> = {
  'user.login.success': 'User Logged In',
  'user.login.failed': 'Failed Login Attempt',
  'user.registered': 'User Registered',
  'user.created': 'User Created',
  'user.updated': 'User Updated',
  'user.role_changed': 'Role Changed',
  'user.deactivated': 'User Deactivated',
  'user.reactivated': 'User Reactivated',
  'user.password_reset': 'Password Reset',
  'user.deleted': 'User Deleted',
  'user.bulk_imported': 'Bulk Import',
  'workshop.created': 'Tech Eval Created',
  'workshop.published': 'Tech Eval Published',
  'workshop.archived': 'Tech Eval Archived',
  'workshop.deleted': 'Tech Eval Deleted',
  'workshop.transferred': 'Tech Eval Transferred',
  'workshop.status_changed': 'Tech Eval Status Changed',
  'session.created': 'Session Created',
  'session.started': 'Session Started',
  'session.ended': 'Session Ended',
  'session.force_ended': 'Session Force Ended',
  'settings.updated': 'Settings Updated',
};

function getActionColor(
  action: string,
): 'info' | 'secondary' | 'warning' | 'success' | 'default' | 'error' {
  if (action.startsWith('user.login.failed') || action === 'user.deactivated') return 'error';
  if (action.startsWith('user.login')) return 'info';
  if (action.startsWith('user.')) return 'secondary';
  if (action.startsWith('workshop.')) return 'warning';
  if (action.startsWith('session.')) return 'success';
  if (action.startsWith('settings.')) return 'default';
  return 'default';
}

// ── Action filter options ──────────────────────────────────────────────────────

const actionFilterOptions = [
  { label: 'All', value: '' },
  { label: 'Login', value: 'user.login' },
  { label: 'User Management', value: 'user.' },
  { label: 'Tech Evals', value: 'workshop.' },
  { label: 'Sessions', value: 'session.' },
  { label: 'Settings', value: 'settings.' },
];

// ── Types ──────────────────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  action: string;
  userId: string | null;
  userEmail: string | null;
  userRole: string | null;
  targetType: string | null;
  targetId: string | null;
  details: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
}

// ── Component ──────────────────────────────────────────────────────────────────

const AdminAudit: React.FC = () => {
  const { showNotification } = useNotification();
  // State
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [actionFilter, setActionFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverContent, setPopoverContent] = useState('');

  // Debounce ref
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedSearchRef = useRef(search);

  // Build params helper
  const buildParams = useCallback(
    (searchOverride?: string) => {
      const params: Record<string, string | number | undefined> = {
        page: page + 1,
        limit,
      };
      if (actionFilter) params.action = actionFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const s = searchOverride ?? debouncedSearchRef.current;
      if (s) params.search = s;
      return params;
    },
    [page, limit, actionFilter, startDate, endDate],
  );

  // Fetch
  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getAuditLog(buildParams());
      const data = res.data as {
        entries: AuditEntry[];
        total: number;
        page: number;
        limit: number;
      };
      setEntries(data.entries);
      setTotal(data.total);
    } catch {
      showNotification('Failed to load audit log', 'error');
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Fetch on mount and when deps change
  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  // Debounce search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      debouncedSearchRef.current = search;
      setPage(0);
      fetchAuditLog();
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Export CSV
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params: Record<string, string | undefined> = {};
      if (actionFilter) params.action = actionFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (debouncedSearchRef.current) params.search = debouncedSearchRef.current;

      const res = await adminService.exportAuditLog(params);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${dayjs().format('YYYY-MM-DD')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      showNotification('Failed to export audit log', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Filter handlers
  const handleActionFilterChange = (e: SelectChangeEvent<string>) => {
    setActionFilter(e.target.value);
    setPage(0);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setPage(0);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setPage(0);
  };

  // Details popover
  const handleDetailsClick = (
    event: React.MouseEvent<HTMLElement>,
    entry: AuditEntry,
  ) => {
    if (expandedRow === entry.id) {
      setExpandedRow(null);
      setPopoverAnchor(null);
      return;
    }
    setExpandedRow(entry.id);
    setPopoverContent(JSON.stringify(entry.details, null, 2));
    setPopoverAnchor(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setExpandedRow(null);
    setPopoverAnchor(null);
  };

  // Helpers
  const truncateDetails = (details: Record<string, unknown> | null): string => {
    if (!details) return '';
    const raw = JSON.stringify(details);
    return raw.length > 60 ? `${raw.slice(0, 60)}...` : raw;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Audit Log</Typography>
        <Button
          variant="outlined"
          startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadOutlined />}
          onClick={handleExport}
          disabled={exportLoading}
        >
          Export CSV
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              label="Action Type"
              value={actionFilter}
              onChange={handleActionFilterChange}
            >
              {actionFilterOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Search details"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220, flex: 1 }}
          />
        </Box>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: '12px' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>IP Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      No audit entries found for the selected filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id} hover>
                    {/* Timestamp */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={dayjs(entry.createdAt).fromNow()} arrow>
                        <span>{dayjs(entry.createdAt).format('MMM D, YYYY HH:mm')}</span>
                      </Tooltip>
                    </TableCell>

                    {/* Action */}
                    <TableCell>
                      <Chip
                        label={actionLabels[entry.action] || entry.action}
                        color={getActionColor(entry.action)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    {/* User */}
                    <TableCell>
                      <Typography variant="body2">
                        {entry.userEmail || 'System'}
                      </Typography>
                      {entry.userRole && (
                        <Typography variant="caption" color="text.secondary">
                          {entry.userRole}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Target */}
                    <TableCell>
                      {entry.targetType ? (
                        <Typography variant="body2">
                          {entry.targetType}
                          {entry.targetId ? ` #${entry.targetId}` : ''}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Details */}
                    <TableCell>
                      {entry.details ? (
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 240,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={(e) => handleDetailsClick(e, entry)}
                        >
                          {truncateDetails(entry.details)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* IP Address */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {entry.ipAddress || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && entries.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
          />
        )}
      </Paper>

      {/* Details Popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 480, maxHeight: 320, overflow: 'auto' }}>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              m: 0,
            }}
          >
            {popoverContent}
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
};

export default AdminAudit;
